import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import { fetchSeasonsPlayed, fetchTeamRegistrations, fetchTeamStanding } from "../../services/TeamService";
import PlayerImage from "../../components/PlayerImage";
import TeamLogo from "../../components/TeamLogo";
import { admins, divisionMappings } from "../../Constants";
import useTeamData from "../../hooks/useTeamData";
import { TournamentConfig, getTournamentConfigsBySeason } from "../../services/TournamentService";
import { getDivisionDisplayName } from "../../utils/divisionUtils";
import '../../styles/theme.css';
import './TeamPageV2.css';
import SeasonScroll from "../../components/SeasonScroll/SeasonScroll";
import TeamTabs from "./TeamTabsV2";
import TeamMatches from "./TeamMatches";
import { Standing } from "../../models/Standing";
import { Account } from "../../models/Account";
import { fetchAccount } from "../../services/AccountService";
import Loading from "../../components/Loading";
import { AuthContext } from "../../context/AuthContext";
import TeamMapStats from "./TeamMapStats";
import TeamStats from "./TeamStats";
import TeamRoles from "./TeamRoles";
import { fetchTeamTransfers } from "../../services/TransferService";
import { RosterTransfer } from "../../models/RosterTransfer";
import { PlayerStats } from "../../models/PlayerStats";
import { fetchPlayersByTeamId } from "../../services/PlayerStatService";
import { pickRating, PickedRating, RATING_KIND_VERSION } from "../../utils/ratingDisplay";

type RosterSide = "all" | "t" | "ct";

/** First season with T/CT splits (SEASON_WITH_SIDES in LeaderboardCacheService.cs). */
const SEASON_WITH_SIDES = 29;

/** The API's side-scoped fields as it actually spells them (t_KD, cT_ADR, ...). */
type SideFields = Partial<Record<`${"t_" | "cT_"}${"Rating30" | "Rating21" | "Rating2" | "ADR" | "KD"}`, number | null>>;

/** The numbers a roster card shows, for the whole map or one side of it. */
const rosterNumbers = (st: PlayerStats, side: RosterSide): { rating: PickedRating | null; adr: number; kd: number } => {
  if (side === "all") return { rating: pickRating(st), adr: st.adr, kd: st.kd };
  const s = st as unknown as SideFields;
  const p = side === "t" ? "t_" : "cT_";
  return {
    rating: pickRating({ rating30: s[`${p}Rating30`], rating21: s[`${p}Rating21`], rating2: s[`${p}Rating2`] }),
    adr: s[`${p}ADR`] ?? 0,
    kd: s[`${p}KD`] ?? 0,
  };
};

const TeamPageV2 = () => {
  const { teamId: urlTeamId } = useParams<{ teamId: string }>();
  const [searchParams] = useSearchParams();
  const urlSeason = parseInt(searchParams.get('season') ?? '', 10);

  // A squad that entered a qualifier and a division has an OverPass id for each, and links use
  // either. Its parsed maps sit under the canonical id while fixtures, transfers and live maps
  // keep the registration's, so the page resolves the URL id once and reads the squad as one.
  const [squad, setSquad] = useState<{ urlId: string; teamId: string; registrationIds: string[] }>();
  useEffect(() => {
    if (!urlTeamId) return;
    let stale = false;
    fetchTeamRegistrations(urlTeamId, isNaN(urlSeason) ? undefined : urlSeason)
      .catch(() => ({ teamId: urlTeamId, registrationIds: [urlTeamId] }))
      .then(r => { if (!stale) setSquad({ urlId: urlTeamId, ...r }); });
    return () => { stale = true; };
  }, [urlTeamId, urlSeason]);
  const resolved = squad?.urlId === urlTeamId ? squad : undefined;
  const teamId = resolved?.teamId;
  const registrationIds = resolved?.registrationIds;

  const [season, setSeason] = useState<number>(0);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [owner, setOwner] = useState<Account | null>(null);
  const [standing, setStanding] = useState<Standing>();
  const [activeTab, setActiveTab] = useState<string>("matches");
  const [seasonsLoading, setSeasonsLoading] = useState(true);
  const [initialLoadStarted, setInitialLoadStarted] = useState(false);
  const [seasonChangeLoading, setSeasonChangeLoading] = useState(false);
  const [transfers, setTransfers] = useState<RosterTransfer[]>([]);
  const [tournamentConfigs, setTournamentConfigs] = useState<TournamentConfig[]>([]);
  const [matchDivisions, setMatchDivisions] = useState<string[]>([]);
  // The Kampe tab's division buttons; the roster shows that division's stats too.
  const [divisionFilter, setDivisionFilter] = useState<string>("all");
  // players null = the fetch failed, so the roster falls back to season totals.
  const [divisionStats, setDivisionStats] = useState<{ key: string; players: PlayerStats[] | null }>();
  const [side, setSide] = useState<RosterSide>("all");

  const { teamData, players, loading: teamDataLoading } = useTeamData(teamId, season);
  const { user: visitor } = useContext(AuthContext);

  const isInitialLoading = !resolved || !initialLoadStarted || seasonsLoading || (season === 0 && seasons.length > 0);

  // ── Data fetching (same as v1) ───────────────────────
  const getSeasonsPlayed = useCallback(async () => {
    if (!teamId) return;
    try {
      setSeasonsLoading(true);
      const fetched = await fetchSeasonsPlayed(teamId);
      if (fetched && fetched.length > 0) {
        setSeasons(fetched);
        setInitialLoadStarted(true);
      }
    } catch (e) {
      console.error("Error fetching seasons:", e);
    } finally {
      setSeasonsLoading(false);
    }
  }, [teamId]);

  const getTeamStanding = useCallback(async () => {
    if (!teamId || season === 0) return;
    try {
      const s = await fetchTeamStanding(teamId, season);
      setStanding(s);
    } catch (_) {}
  }, [teamId, season]);

  useEffect(() => {
    if (teamData) {
      if (teamData.ownerId) fetchAccount(teamData.ownerId).then(a => setOwner(a || null)).catch(() => {});
      else setOwner(null);
    }
  }, [teamData]);

  useEffect(() => { getSeasonsPlayed(); }, [getSeasonsPlayed]);

  useEffect(() => {
    if (seasons.length === 0) return;
    const q = searchParams.get('season');
    const parsed = q ? parseInt(q, 10) : NaN;
    if (!isNaN(parsed) && seasons.includes(parsed)) setSeason(parsed);
    else setSeason(Math.max(...seasons));
  }, [seasons, searchParams]);

  useEffect(() => {
    if (season === 0) return;
    const go = async () => { setSeasonChangeLoading(true); await getTeamStanding(); setSeasonChangeLoading(false); };
    go();
  }, [getTeamStanding, season]);

  useEffect(() => {
    if (!teamId || season === 0) return;
    fetchTeamTransfers(teamId, season).then(setTransfers).catch(() => setTransfers([]));
  }, [teamId, season]);

  useEffect(() => {
    if (season === 0) return;
    setMatchDivisions([]);
    setDivisionFilter("all");
    getTournamentConfigsBySeason(season).then(setTournamentConfigs).catch(() => setTournamentConfigs([]));
  }, [season]);

  // Per-division stats need per-map rows, which exist from season 26.
  const divisionFilterActive = divisionFilter !== "all" && season >= 26;
  const divisionKey = `${teamData?.teamId}|${season}|${divisionFilter}`;

  useEffect(() => {
    if (!teamData || !divisionFilterActive) return;
    let stale = false;
    fetchPlayersByTeamId(teamData.teamId, season, divisionFilter)
      .then(ps => { if (!stale) setDivisionStats({ key: divisionKey, players: ps }); })
      .catch(err => {
        console.error("Error fetching division stats:", err);
        if (!stale) setDivisionStats({ key: divisionKey, players: null });
      });
    return () => { stale = true; };
  }, [teamData, season, divisionFilter, divisionFilterActive, divisionKey]);

  // ── Helpers ──────────────────────────────────────────
  const getRating = (p: PlayerStats) => pickRating(p)?.value ?? 0;
  const playersWithGames = (arr: PlayerStats[]) => arr.filter(p => p.gamesPlayed > 0);
  const avgRating = (arr: PlayerStats[]) => {
    const active = playersWithGames(arr);
    return active.length > 0 ? (active.reduce((s, p) => s + getRating(p), 0) / active.length).toFixed(2) : "0.00";
  };
  const avgElo = (arr: PlayerStats[]) => {
    const with_ = arr.filter(p => p.account?.faceitElo);
    return with_.length > 0 ? Math.round(with_.reduce((s, p) => s + (p.account?.faceitElo || 0), 0) / with_.length).toString() : "—";
  };

  // Transfers are tracked per OverPass registration, so match them against every id the squad has.
  const isOurs = (id: string | null) => id !== null && (registrationIds ?? []).includes(id);
  const getJoinDate = (steamId: string) => {
    const t = transfers.find(t => t.playerSteamId === steamId && isOurs(t.toTeamId));
    if (t) return new Date(t.transferDate).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
    const all = transfers.filter(t => isOurs(t.toTeamId) || isOurs(t.fromTeamId));
    if (all.length > 0) return new Date(Math.min(...all.map(t => new Date(t.transferDate).getTime()))).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
    return null;
  };
  const getLeaveDate = (steamId: string) => {
    const t = transfers.find(t => t.playerSteamId === steamId && isOurs(t.fromTeamId));
    return t ? new Date(t.transferDate).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' }) : null;
  };

  const isAdmin = admins.includes(visitor?.steamID ?? "");

  // ── Loading state ────────────────────────────────────
  if (isInitialLoading) {
    return <div className="container text-center py-5"><Loading /><p className="mt-3" style={{ color: 'rgba(255,255,255,0.5)' }}>Henter holddata...</p></div>;
  }

  const divisionLoaded = divisionFilterActive && divisionStats?.key === divisionKey;
  const divisionPlayers = divisionLoaded ? divisionStats!.players : null;
  const rosterLoading = divisionFilterActive && !divisionLoaded;
  // The roster is always the full season's; a division view swaps each card's numbers, and a
  // player with no maps in that division has none.
  const statsFor = (p: PlayerStats) => divisionPlayers ? divisionPlayers.find(d => d.steamID === p.steamID) : p;
  const mapsFor = (p: PlayerStats) => statsFor(p)?.gamesPlayed ?? 0;

  // Highest rating first, but only among players with a real sample: at least half the maps of
  // the most-played player in view. The rest follow, so a sub's big rating off 2 maps can't lead.
  const maxMaps = Math.max(0, ...players.map(mapsFor));
  const sampleTier = (p: PlayerStats) => {
    const maps = mapsFor(p);
    return maps === 0 ? 2 : maps * 2 >= maxMaps ? 0 : 1;
  };
  // Older seasons have no side split, so a T/CT pick quietly falls back to the whole map there.
  const rosterSide: RosterSide = season >= SEASON_WITH_SIDES ? side : "all";
  // Ordered by the overall rating whatever the side, so flipping T/CT compares each card in place.
  const ratingFor = (p: PlayerStats) => { const st = statsFor(p); return st ? getRating(st) : 0; };
  const sortedPlayers = [...players].sort((a, b) => sampleTier(a) - sampleTier(b) || ratingFor(b) - ratingFor(a));
  const loading = teamDataLoading || seasonChangeLoading;

  return (
    <div className="container" style={{ marginBottom: 60 }}>

      {/* ── Header ──────────────────────────────── */}
      {teamData && (
        <div className={loading ? 'tv2-dimmed' : 'tv2-normal'}>
          <div className="tv2-header">
            <TeamLogo logo={teamData.teamLogo} name={teamData.teamName} teamId={teamData.teamId} className="tv2-logo" />

            <div className="tv2-info">
              <h1 className="tv2-team-name">{teamData.teamName}</h1>
              <div className="tv2-meta">
                {(() => {
                  const mainDivName = divisionMappings.get(teamData.division);
                  const hasStandardDiv = !!mainDivName;
                  const hasLigaen = teamData.playsInLigaen && teamData.division !== "ligaen";
                  const kvalDivisions = matchDivisions.filter(d => d.includes("kval"));
                  const hasKval = kvalDivisions.length > 0;

                  // Main division label
                  const mainLabel = hasStandardDiv
                    ? <Link to={`/standing/${season}/${teamData.division}`} className="tv2-division">{mainDivName}</Link>
                    : hasKval
                      ? <span className="tv2-division">{kvalDivisions.length === 1 ? getDivisionDisplayName(kvalDivisions[0], tournamentConfigs) : "Liga Kvals"}</span>
                      : <span className="tv2-division">{getDivisionDisplayName(teamData.division, tournamentConfigs)}</span>;

                  // Extra label: ligaen takes priority, kval only if no ligaen and team has a standard main div
                  const extraLabel = hasLigaen
                    ? <Link to={`/standing/${season}/ligaen`} className="tv2-division">+ Ligaen</Link>
                    : (hasStandardDiv && hasKval && !hasLigaen)
                      ? <span className="tv2-division">+ {kvalDivisions.length === 1 ? getDivisionDisplayName(kvalDivisions[0], tournamentConfigs) : "Liga Kvals"}</span>
                      : null;

                  return <>{mainLabel}{extraLabel}</>;
                })()}
                {standing && (
                  <><span className="tv2-meta-sep">|</span><span>#{standing.rank}</span></>
                )}
                <span className="tv2-meta-sep">|</span>
                <span>ELO <span className="tv2-meta-val">{avgElo(players)}</span></span>
                <span className="tv2-meta-sep">|</span>
                <span>Rating <span className="tv2-meta-val">{avgRating(divisionPlayers ?? players)}</span></span>
              </div>
              {(teamData.twitter || teamData.website) && (
                <div className="tv2-socials">
                  {teamData.twitter && <a href={teamData.twitter.startsWith("http") ? teamData.twitter : `https://x.com/${teamData.twitter}`} target="_blank" rel="noreferrer" className="tv2-social-link">@{teamData.twitter}</a>}
                  {teamData.website && <a href={teamData.website.startsWith("http") ? teamData.website : `https://${teamData.website}`} target="_blank" rel="noreferrer" className="tv2-social-link">{teamData.website}</a>}
                </div>
              )}
            </div>

            <div className="tv2-header-right">
              {owner && (
                <Link to={`/player/${owner.steamID}`} className="tv2-owner-chip">
                  <PlayerImage id={owner.steamID} width={40} />
                  <span className="tv2-owner-name">{owner.playerName}</span>
                  <span className="tv2-owner-label">Holdejer</span>
                </Link>
              )}
              {isAdmin && (
                <Link to={`/team/${teamId}/edit?season=${season}`} className="btn btn-sm" style={{ background: 'rgba(255,102,0,0.15)', color: '#ff6600', border: '1px solid rgba(255,102,0,0.3)', fontSize: '0.75rem' }}>
                  Rediger
                </Link>
              )}
            </div>
          </div>
          <hr className="tv2-divider" />
        </div>
      )}

      {/* ── Roster strip ────────────────────────── */}
      <div className={loading || rosterLoading ? 'tv2-dimmed' : 'tv2-normal'}>
        <div className="tv2-roster-head">
          <div className="tv2-roster-caption">
            Spillerstats · <span className="tv2-roster-caption-val">
              {divisionPlayers ? getDivisionDisplayName(divisionFilter, tournamentConfigs) : "Hele sæsonen"}
            </span>
            {rosterSide !== "all" && (
              <span className={`tv2-side-${rosterSide}`} style={{ fontWeight: 600 }}>
                {" · "}{rosterSide === "t" ? "T-side" : "CT-side"}
              </span>
            )}
          </div>
          {season >= SEASON_WITH_SIDES && (
            <div className="tv2-side-toggle" role="group" aria-label="Side">
              {(["all", "t", "ct"] as RosterSide[]).map(s => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={side === s}
                  className={`tv2-side-btn ${side === s ? `tv2-side-btn--${s}` : ""}`}
                  onClick={() => setSide(s)}
                >
                  {s === "all" ? "All" : s.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="tv2-roster">
          {sortedPlayers.map(p => {
            const join = getJoinDate(p.steamID);
            const leave = getLeaveDate(p.steamID);
            const st = statsFor(p);
            const nums = st && st.gamesPlayed > 0 ? rosterNumbers(st, rosterSide) : null;
            const hasNotPlayed = !nums;
            return (
              <Link key={p.steamID} to={`/player/${p.steamID}`} className="tv2-player-card" style={hasNotPlayed ? { opacity: 0.7 } : undefined}>
                <PlayerImage id={p.steamID} width={72} />
                <span className="tv2-player-name">{p.account?.playerName || p.steamID}</span>
                {!st || !nums ? (
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic', marginTop: 8 }}>
                    {divisionPlayers ? "Ingen kampe i denne division" : "Ingen kampe endnu"}
                  </span>
                ) : (
                  <>
                    <div className="tv2-player-stats">
                      <span className={`tv2-player-rating ${rosterSide !== "all" ? `tv2-side-${rosterSide}` : ""}`}>
                        {nums.rating ? nums.rating.value.toFixed(2) : "–"}
                      </span>
                      <span className="tv2-player-sub">Rating {RATING_KIND_VERSION[(nums.rating ?? pickRating(st))?.kind ?? "r2"]}</span>
                    </div>
                    <div className="tv2-player-row">
                      <div className="tv2-player-stat-mini">
                        <span className="tv2-player-stat-mini-val">{st.gamesPlayed}</span>
                        <span className="tv2-player-stat-mini-label">Kampe</span>
                      </div>
                      <div className="tv2-player-stat-mini">
                        <span className="tv2-player-stat-mini-val">{nums.adr.toFixed(0)}</span>
                        <span className="tv2-player-stat-mini-label">ADR</span>
                      </div>
                      <div className="tv2-player-stat-mini">
                        <span className="tv2-player-stat-mini-val">{nums.kd.toFixed(2)}</span>
                        <span className="tv2-player-stat-mini-label">K/D</span>
                      </div>
                    </div>
                  </>
                )}
                {join && (
                  <span className="tv2-player-date" style={{ color: leave ? '#f87171' : '#4ade80' }}>
                    {join} → {leave || 'nu'}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Season + Tabs bar ───────────────────── */}
      <div className="tv2-controls">
        <SeasonScroll
          seasons={seasons}
          selectedSeason={season}
          onSeasonChange={s => { if (s !== season) setSeason(s); }}
          isLoading={loading}
        />
        <TeamTabs onTabChange={t => { setActiveTab(t); setDivisionFilter("all"); }} />
      </div>

      {/* ── Tab content ─────────────────────────── */}
      <div className="tv2-content">
        {activeTab === "matches" && (
          <>
            <div className={loading ? 'tv2-dimmed' : 'tv2-normal'}>
              <TeamMatches key={season} season={season} teamId={teamId} registrationIds={registrationIds}
                tournamentConfigs={tournamentConfigs} onDivisionsLoaded={setMatchDivisions}
                divisionFilter={divisionFilter} onDivisionFilterChange={setDivisionFilter} />
            </div>
            {loading && <div className="tv2-overlay"><Loading /></div>}
          </>
        )}

        {activeTab === "veto" && (
          <>
            <div className={loading ? 'tv2-dimmed' : 'tv2-normal'}>
              <TeamMapStats season={season} teamId={teamId} />
            </div>
            {loading && <div className="tv2-overlay"><Loading /></div>}
          </>
        )}

        {activeTab === "roles" && (
          <>
            <div className={loading ? 'tv2-dimmed' : 'tv2-normal'}>
              <TeamRoles players={players} season={season} />
            </div>
            {loading && <div className="tv2-overlay"><Loading /></div>}
          </>
        )}

        {activeTab === "stats" && (
          <>
            <div className={loading ? 'tv2-dimmed' : 'tv2-normal'}>
              <TeamStats season={season} teamId={teamId} />
            </div>
            {loading && <div className="tv2-overlay"><Loading /></div>}
          </>
        )}
      </div>
    </div>
  );
};

export default TeamPageV2;
